import { useState, useEffect } from 'react';
import { adminApi, lookupsApi } from '../services/api';
import TopNav from '../components/layout/TopNav';

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | 'edit' | 'reset'
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ fullName: '', email: '', role: 'Processor', password: '', confirm: '' });
  const [formError, setFormError] = useState('');
  const [toast, setToast] = useState('');

  // Loan types state
  const [loanTypes, setLoanTypes] = useState([]);
  const [ltModal, setLtModal] = useState(null); // null | 'new' | 'edit'
  const [ltEditId, setLtEditId] = useState(null);
  const [ltName, setLtName] = useState('');
  const [ltError, setLtError] = useState('');

  useEffect(() => { loadUsers(); loadLoanTypes(); }, []);

  async function loadLoanTypes() {
    try {
      const data = await lookupsApi.getAll();
      setLoanTypes(data.loanTypes || []);
    } catch (err) { console.error(err); }
  }

  async function loadUsers() {
    setLoading(true);
    try { setUsers(await adminApi.getUsers()); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  function openNew() {
    setForm({ fullName: '', email: '', role: 'Processor', password: '', confirm: '' });
    setFormError('');
    setEditUser(null);
    setModal('new');
  }

  function openEdit(u) {
    setForm({ fullName: u.fullName, email: u.email, role: u.role, password: '', confirm: '' });
    setFormError('');
    setEditUser(u);
    setModal('edit');
  }

  function openReset(u) {
    setForm({ fullName: u.fullName, email: u.email, role: u.role, password: '', confirm: '' });
    setFormError('');
    setEditUser(u);
    setModal('reset');
  }

  async function handleRoleChange(u, role) {
    try {
      await adminApi.updateRole(u.id, role);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, role } : x));
      showToast(`Role updated for ${u.fullName}.`);
    } catch (err) { showToast(err.response?.data?.message || 'Failed to update role.'); }
  }

  async function handleToggleActive(u) {
    try {
      if (u.isActive) await adminApi.deactivate(u.id);
      else await adminApi.activate(u.id);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, isActive: !x.isActive } : x));
      showToast(`${u.fullName} ${u.isActive ? 'deactivated' : 'reactivated'}.`);
    } catch (err) { showToast(err.response?.data?.message || 'Failed to update user status.'); }
  }

  async function handleSave() {
    setFormError('');

    if (modal === 'new') {
      if (!form.fullName || !form.email) { setFormError('Name and email are required.'); return; }
      if (!form.email.includes('@')) { setFormError('Enter a valid email address.'); return; }
      if (!form.password) { setFormError('Password is required.'); return; }
    }

    if (modal === 'edit') {
      if (!form.fullName || !form.email) { setFormError('Name and email are required.'); return; }
      if (!form.email.includes('@')) { setFormError('Enter a valid email address.'); return; }
    }

    if (modal === 'reset' || (modal === 'new' && form.password)) {
      if (form.password.length < 8) { setFormError('Password must be at least 8 characters.'); return; }
      if (form.password !== form.confirm) { setFormError('Passwords do not match.'); return; }
    }

    try {
      if (modal === 'new') {
        const newUser = await adminApi.createUser({
          fullName: form.fullName, email: form.email,
          password: form.password, role: form.role,
        });
        setUsers(prev => [...prev, newUser]);
        showToast(`${form.fullName} added successfully.`);
      } else if (modal === 'edit') {
        const updated = await adminApi.updateUser(editUser.id, {
          fullName: form.fullName, email: form.email,
        });
        setUsers(prev => prev.map(x => x.id === editUser.id ? { ...x, ...updated } : x));
        showToast(`${form.fullName} updated successfully.`);
      } else {
        await adminApi.resetPassword(editUser.id, form.password);
        showToast('Password reset successfully.');
      }
      setModal(null);
    } catch (err) { setFormError(err.response?.data?.message || 'An error occurred.'); }
  }

  function openLtNew() {
    setLtName(''); setLtError(''); setLtEditId(null); setLtModal('new');
  }
  function openLtEdit(lt) {
    setLtName(lt.name); setLtError(''); setLtEditId(lt.id); setLtModal('edit');
  }
  async function saveLoanType() {
    const name = ltName.trim();
    if (!name) { setLtError('Name is required.'); return; }
    try {
      if (ltModal === 'new') {
        await lookupsApi.createLoanType(name);
        showToast(`"${name}" added.`);
      } else {
        await lookupsApi.updateLoanType(ltEditId, name);
        showToast(`"${name}" updated.`);
      }
      setLtModal(null);
      loadLoanTypes();
    } catch (err) { setLtError(err.response?.data?.message || 'An error occurred.'); }
  }
  async function deleteLoanType(lt) {
    if (!window.confirm(`Remove "${lt.name}"? Existing files using this type will not be affected.`)) return;
    try {
      await lookupsApi.deleteLoanType(lt.id);
      showToast(`"${lt.name}" removed.`);
      loadLoanTypes();
    } catch (err) { showToast(err.response?.data?.message || 'Cannot remove this loan type.'); }
  }

  const modalTitle = modal === 'new' ? 'New user'
    : modal === 'edit' ? `Edit user — ${editUser?.fullName}`
    : `Reset password — ${editUser?.fullName}`;

  return (
    <div>
      <TopNav />
      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <h2>Admin panel</h2>
          <button className="btn btn-primary btn-sm" onClick={openNew} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New user
          </button>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8f9fc' }}>
                {['Name', 'Email', 'Role', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '9px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#888', letterSpacing: '0.04em', borderBottom: '1px solid #e0e4ec' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Loading...</td></tr>
              ) : users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #f0f2f6' }}>
                  <td style={{ padding: '11px 16px', fontWeight: 500 }}>{u.fullName}</td>
                  <td style={{ padding: '11px 16px', color: '#666' }}>{u.email}</td>
                  <td style={{ padding: '11px 16px' }}>
                    <select
                      value={u.role}
                      onChange={e => handleRoleChange(u, e.target.value)}
                      style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #d0d5e0', borderRadius: 6, background: '#fff', width: 'auto' }}
                    >
                      <option>Processor</option>
                      <option>Admin</option>
                    </select>
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <span className={`badge ${u.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm" style={{ fontSize: 11 }} onClick={() => openEdit(u)}>Edit</button>
                      <button className="btn btn-sm" style={{ fontSize: 11 }} onClick={() => openReset(u)}>Reset password</button>
                      <button
                        className={`btn btn-sm${u.isActive ? '' : ' btn-primary'}`}
                        style={{ fontSize: 11 }}
                        onClick={() => handleToggleActive(u)}
                      >
                        {u.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* LOAN TYPES */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '2rem', marginBottom: '1.25rem' }}>
          <div>
            <h3 style={{ marginBottom: 3 }}>Loan types</h3>
            <div style={{ fontSize: 12, color: '#888' }}>Options available in the Loan type dropdown on all files</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={openLtNew} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add loan type
          </button>
        </div>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8f9fc' }}>
                {['Loan type', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '9px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#888', letterSpacing: '0.04em', borderBottom: '1px solid #e0e4ec' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loanTypes.length === 0 ? (
                <tr><td colSpan={3} style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>No loan types defined.</td></tr>
              ) : loanTypes.map(lt => (
                <tr key={lt.id} style={{ borderBottom: '1px solid #f0f2f6' }}>
                  <td style={{ padding: '11px 16px', fontWeight: 500 }}>{lt.name}</td>
                  <td style={{ padding: '11px 16px' }}>
                    <span className={`badge ${lt.isActive ? 'badge-active' : 'badge-inactive'}`}>{lt.isActive ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm" style={{ fontSize: 11 }} onClick={() => openLtEdit(lt)}>Edit</button>
                      {lt.isActive && (
                        <button className="btn btn-sm" style={{ fontSize: 11, color: '#991b1b', borderColor: '#fca5a5' }} onClick={() => deleteLoanType(lt)}>Remove</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Loan Type Modal */}
      {ltModal && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setLtModal(null); }}>
          <div className="modal-box" style={{ width: 420 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: '1.25rem' }}>
              {ltModal === 'new' ? 'Add loan type' : 'Edit loan type'}
            </div>
            <div className="fg">
              <label>Loan type name *</label>
              <input value={ltName} onChange={e => setLtName(e.target.value)} placeholder="e.g. Conventional" maxLength={100} autoFocus />
            </div>
            {ltError && (
              <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginTop: 6 }}>{ltError}</div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={saveLoanType}>{ltModal === 'new' ? 'Add loan type' : 'Save changes'}</button>
              <button className="btn" onClick={() => setLtModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal — New user / Edit user / Reset password */}
      {modal && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div className="modal-box">
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: '1.25rem' }}>{modalTitle}</div>

            {/* Name + Email — shown for new and edit */}
            {(modal === 'new' || modal === 'edit') && (
              <div className="row2" style={{ marginBottom: 12 }}>
                <div className="fg" style={{ marginBottom: 0 }}>
                  <label>Full name *</label>
                  <input value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} placeholder="Jane Smith" />
                </div>
                <div className="fg" style={{ marginBottom: 0 }}>
                  <label>Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@company.com" />
                </div>
              </div>
            )}

            {/* Role — shown for new only */}
            {modal === 'new' && (
              <div className="fg">
                <label>Role</label>
                <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={{ width: 'auto' }}>
                  <option>Processor</option><option>Admin</option>
                </select>
              </div>
            )}

            {/* Password section — shown for new and reset */}
            {(modal === 'new' || modal === 'reset') && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, paddingTop: 4, borderTop: modal === 'new' ? '1px solid #eee' : 'none', marginTop: modal === 'new' ? 4 : 0 }}>
                  {modal === 'new' ? 'Password' : 'New password'}
                </div>
                <div className="row2">
                  <div className="fg" style={{ marginBottom: 0 }}>
                    <label>{modal === 'new' ? 'Password *' : 'New password *'}</label>
                    <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min. 8 characters" />
                  </div>
                  <div className="fg" style={{ marginBottom: 0 }}>
                    <label>Confirm password *</label>
                    <input type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Re-enter password" />
                  </div>
                </div>
              </>
            )}

            {formError && (
              <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginTop: 10 }}>{formError}</div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleSave}>
                {modal === 'new' ? 'Create user' : modal === 'edit' ? 'Save changes' : 'Save password'}
              </button>
              <button className="btn" onClick={() => setModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 999, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', background: '#dcfce7', color: '#166534' }}>
          {toast}
        </div>
      )}
    </div>
  );
}
